<template>
    <div>
        <roles-create
            id="create-roles-modal"
            ref="modal"
            @createRole="getRoles"
        >
        </roles-create>
        <b-card>
            <datatable-header
                :title="$tc('modules.roles.description', 2)"
                :genericSearchEnabled="true"
                :searchTermPlaceHolder="$t('common.search')"
                @filterUpdated="onFilterUpdate"
            >
                <template #buttons>
                    <b-button
                        class="ml-2"
                        variant="primary"
                        style="background-color: #519bd6"
                        @click="
                            $root.$emit(
                                'bv::toggle::modal',
                                'create-roles-modal'
                            )
                        "
                    >
                        {{
                            $t("common.crud.addNew", {
                                resource: $tc(
                                    "modules.roles.description",
                                    1
                                ).toLowerCase(),
                            }).toString()
                        }}
                    </b-button>
                </template>

                <template #advancedSearch="{ expanded, onFilterChange }">
                    <DatatableFilter
                        :availableDatatableFilters="availableDatatableFilters"
                        :expanded="expanded"
                        @filterUpdated="onFilterUpdate"
                        @onFilterChange="onFilterChange"
                    ></DatatableFilter>
                </template>
            </datatable-header>

            <!-- table -->
            <datatable
                :columns="columns"
                :isLoading="isLoading"
                :paginationOptions="{
                    enabled: true,
                    mode: 'pages',
                    perPage: dataTableOptions.pagination.perPage,
                }"
                :rows="rows"
                :rtl="direction"
                :searchOptions="{
                    enabled: true,
                    externalQuery: searchTerm,
                }"
                :totalRows="dataTableOptions.pagination.totalRows"
                mode="remote"
                theme="my-theme"
                @on-row-click="onRowClick"
                @on-page-change="handleChangePage"
                @onPageSizeChange="handlePageSizeChange"
            >
                <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'roles'">
                        <span v-for="role in props.row.roles" :key="role.id">
                            <b-badge>
                                {{ role.name }}
                            </b-badge>
                        </span>
                    </span>
                    <span v-else>
                        {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </datatable>
        </b-card>
    </div>
</template>

<script>
import { BBadge, BCard, BButton } from "bootstrap-vue"
import store from "@/store/index"
import Api from "@/services/api/index"
import Datatable from "@/views/components/utilis-spa/datatable/Datatable.vue"
import DatatableFilterUtils from "@/views/components/utilis-spa/datatable/datatableFilterUtil"
import RolesCreate from "@/views/app/management/roles/RolesCreate.vue"
import DatatableHeader from "@/views/components/utilis-spa/datatable/DatatableHeader.vue"
import DatatableFilter from "@/views/components/utilis-spa/datatable/DatatableFilter.vue"

export default {
    components: {
        DatatableFilter,
        Datatable,
        BCard,
        BBadge,
        BButton,
        RolesCreate,
        DatatableHeader,
    },
    data() {
        return {
            dataTableOptions: {
                pagination: {
                    perPage: 5,
                    totalRows: 0,
                },
            },
            isLoading: true,
            dir: false,
            isFilterExpanded: false,
            columns: [
                {
                    label: this.$t("common.crud.id"),
                    field: "id",
                },
                {
                    label: this.$t("common.crud.name"),
                    field: "name",
                },
                {
                    label: this.$t("common.crud.createdAt"),
                    field: "createdAt",
                    formatFn: this.formatDateTime,
                },
                {
                    label: this.$t("common.crud.updatedAt"),
                    field: "updatedAt",
                    formatFn: this.formatDateTime,
                },
            ],
            rows: [],
            searchTerm: "",
            availableDatatableFilters: {},
            selectedDatatableFilters: {},
            nameFilter: "",
        }
    },
    computed: {
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        },
    },
    mounted() {
        this.getRoles()
        this.getAvailableFilters()
    },
    methods: {
        formatDateTime(date) {
            // return date.format('L LT')
            // return this.$d(date, 'short')
            if (date == null) return

            // eslint-disable-next-line consistent-return
            return this.$d(new Date(date), "dateTime")
        },
        handleChangePage(page) {
            this.isLoading = true
            this.getRoles({ selectedPage: page.currentPage })
        },
        handlePageSizeChange(pageSize) {
            this.dataTableOptions.pagination.perPage = pageSize
            this.getRoles()
        },
        async getRoles(options = {}) {
            try {
                this.isLoading = true
                const { pagination: paginationOptions } = this.dataTableOptions
                const { selectedPage } = options

                const rolesResponse = await Api.findAll("roles", {
                    // RELATED RESOURCES

                    // FILTERS
                    filter: this.apiFilter,

                    // PAGINATION
                    page: {
                        size: paginationOptions.perPage,
                        number: selectedPage || 1,
                    },
                })

                this.rows = rolesResponse.data
                const paginationMetaData = rolesResponse.meta.page
                this.dataTableOptions.pagination.totalRows =
                    paginationMetaData.total
            } finally {
                this.isLoading = false
            }
        },

        async getAvailableFilters() {
            this.availableDatatableFilters.name = DatatableFilterUtils.createFilter.text(
                "name",
                {
                    label: this.$t("common.crud.name"),
                    placeholder: this.$t("common.searchBy", {
                        field: this.$t("common.crud.name").toLowerCase(),
                    }),
                }
            )
        },
        onRowClick(params) {
            this.$router.push({
                name: "edit-roles",
                params: {
                    id: params.row.id,
                    role: params.row,
                },
            })
        },
        onFilterExpandedChange() {
            this.isFilterExpanded = !this.isFilterExpanded
        },
        onFilterUpdate(updatedFilter) {
            this.apiFilter = DatatableFilterUtils.serializer.datatableFilterToJsonApi(
                updatedFilter
            )
            this.getRoles()
        },
    },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.skeletonTable {
    position: relative;
    top: -50px;
    margin: 0px 20px;
}

.vgt-inner-wrap.is-loading {
    opacity: 1;
}
</style>
